<template>
  <ButtonAddToCart
    v-if="isPurchasable"
    v-bind="$attrs"
    :disabled="isDisabled"
    :on-action="onAddToCartClick"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { useKits } from '@/shared/composables/useKits';
import { useCartStore } from '@/shared/pinia/modules/cart';
import { useProductStore } from '@/shared/pinia/modules/product';
import { trackKitAddedToCart } from '@/shared/tracking/kit-add-to-cart';
import { notifyBugsnagWithContext } from '@/shared/utils/error';

import ButtonAddToCart from '../ButtonAddToCart.vue';
import {
  buildFlyBundlesPayload,
  hashKitVariantSelection
} from './kitCartHelpers';

defineOptions({ inheritAttrs: false });

const cartStore = useCartStore();
const productStore = useProductStore();

const flyConfigVariantIds =
  window.Hobbii?.productPage?.flyConfigVariantIds ?? {};

const kitItemGroups = computed(() =>
  Object.values(flyConfigVariantIds).map((variantIds) =>
    variantIds.filter(Boolean)
  )
);

const kitItems = computed(() =>
  kitItemGroups.value.flat().map((variantId) => ({ variantId, quantity: 1 }))
);

const { resolvedItems, loading } = useKits(kitItems);

const isPurchasable = computed(() => productStore.purchasable);

// A kit product is available if any one of its variants is in stock: an
// unselected color being OOS shouldn't disable the whole bundle.
const allAvailable = computed(() => {
  if (loading.value || kitItemGroups.value.length === 0) return false;

  let index = 0;
  return kitItemGroups.value.every((group) => {
    const groupItems = resolvedItems.value.slice(index, index + group.length);
    index += group.length;
    return (
      groupItems.length === 0 ||
      groupItems.some((item) => item.availableForSale)
    );
  });
});

const isDisabled = computed(() => !allAvailable.value);

const onAddToCartClick = async () => {
  const gfb = window.gfb;
  if (!gfb) {
    notifyBugsnagWithContext(
      'KitAddToCartButton',
      new Error('FlyBundles gfb global not found')
    );
    return;
  }

  const state = gfb.gfbFrequentBought.state;
  const flyOfferId = state.activeOffer[0];
  if (!flyOfferId) {
    notifyBugsnagWithContext(
      'KitAddToCartButton',
      new Error('No active FlyBundles offer')
    );
    return;
  }

  const offerData = state.frequentBoughtDataByOfferId[flyOfferId];
  if (!offerData) {
    notifyBugsnagWithContext(
      'KitAddToCartButton',
      new Error(`FlyBundles offer data not found for ${flyOfferId}`)
    );
    return;
  }

  const flySectionId = window.Hobbii?.productPage?.flySectionId;
  if (!flySectionId) {
    notifyBugsnagWithContext(
      'KitAddToCartButton',
      new Error('FlyBundles section ID missing')
    );
    return;
  }

  // Missing entry is normal for bundles where no products have a variant picker.
  const offerOptions = state.currentSelectedOptions[flyOfferId] ?? {};

  // FlyBundles appends <> + zero-padded index to the handle when the same
  // product occupies multiple slots (e.g. "handle<>00", "handle<>01").
  // Single-occurrence products use the bare handle.
  const handleCount = new Map<string, number>();
  for (const product of offerData.selectedProducts) {
    handleCount.set(product.handle, (handleCount.get(product.handle) ?? 0) + 1);
  }
  const handleIndex = new Map<string, number>();

  const items: Array<{ variantId: number; quantity: number }> = [];
  // FlyBundles populates currentSelectedOptions for every slot, even when a
  // product has only one variant and offers no real picker. Only slots whose
  // product actually has more than one variant represent a genuine choice.
  let hasVariantPicker = false;
  for (const product of offerData.selectedProducts) {
    const count = handleCount.get(product.handle) ?? 1;
    const idx = handleIndex.get(product.handle) ?? 0;
    handleIndex.set(product.handle, idx + 1);

    const slotKey =
      count > 1
        ? `${product.handle}<>${String(idx).padStart(2, '0')}`
        : product.handle;

    const shopifyProduct = state.frequentBoughtShopifyData.find(
      (p) => p.handle === product.handle
    );
    if (!shopifyProduct) {
      notifyBugsnagWithContext(
        'KitAddToCartButton',
        new Error(`Product "${product.handle}" not found in FlyBundles data`)
      );
      return;
    }

    const productVariants =
      state.variantsDataByOptions[String(shopifyProduct.id)];
    if (productVariants && Object.keys(productVariants).length > 1) {
      hasVariantPicker = true;
    }
    const optionName = offerOptions[slotKey]?.values[0];

    // Single-variant products (no picker) have no currentSelectedOptions entry;
    // fall back to the only available variant.
    const variantData = optionName
      ? productVariants?.[optionName]
      : (() => {
          const keys = productVariants ? Object.keys(productVariants) : [];
          return keys.length === 1 ? productVariants![keys[0]] : undefined;
        })();

    if (!variantData) {
      notifyBugsnagWithContext(
        'KitAddToCartButton',
        new Error(`Variant not found for "${product.handle}" slot "${slotKey}"`)
      );
      return;
    }

    items.push({ variantId: variantData.id, quantity: product.quantity });
  }

  const mergedItems = [
    ...items
      .reduce((map, item) => {
        const existing = map.get(item.variantId);
        map.set(
          item.variantId,
          existing
            ? {
                variantId: item.variantId,
                quantity: existing.quantity + item.quantity
              }
            : item
        );
        return map;
      }, new Map<number, { variantId: number; quantity: number }>())
      .values()
  ];

  // When the bundle has genuinely variant-selectable products, each
  // add-to-cart call must use an offer ID that reflects the selected variant
  // combination, so FlyBundles' cart transform merges repeat adds of the SAME
  // combination but keeps DIFFERENT combinations as separate bundles.
  const effectiveFlyOfferId = hasVariantPicker
    ? `${flyOfferId}_${hashKitVariantSelection(mergedItems)}`
    : flyOfferId;

  const payload = buildFlyBundlesPayload(
    mergedItems,
    effectiveFlyOfferId,
    flySectionId
  );

  await cartStore.addToCart(payload as ShopifyCartAddPayload);
  trackKitAddedToCart();
};
</script>
